// Treasury deposit-requests (recharge) module endpoint functions.
//
// Endpoints accept camelCase view-model inputs and return camelCase
// view-model outputs. Snake_case lives ONLY between the network and
// the parser.

import { adminApi } from '@core/api/httpClient'
import { DEPOSIT_REQUESTS_ENDPOINT_PATHS } from './depositRequestsEndpointPaths'
import type {
  ApproveDepositRequestPayloadDto,
  DepositRequestDetailDto,
  DepositRequestRowDto,
  DepositRequestStatusCountsDto,
  DepositRequestsListResponseDto,
  RejectDepositRequestPayloadDto,
} from './depositRequestsContracts'
import type {
  ApproveDepositRequestInput,
  DepositRequestDetail,
  DepositRequestRow,
  DepositRequestStatusCounts,
  DepositRequestsListResponse,
  GetDepositRequestsParams,
  RejectDepositRequestInput,
} from '../types/depositRequestsTypes'
import {
  parseDepositRequestDetail,
  parseDepositRequestRow,
  parseDepositRequestStatusCounts,
  parseDepositRequestsListResponse,
} from './depositRequestsParser'

type WireListResponse = DepositRequestsListResponseDto | { data: DepositRequestsListResponseDto }
type WireDetailResponse = DepositRequestDetailDto | { data: DepositRequestDetailDto }
type WireRowResponse = DepositRequestRowDto | { data: DepositRequestRowDto }
type WireStatusCounts = DepositRequestStatusCountsDto | { data: DepositRequestStatusCountsDto }

function unwrap<T>(payload: T | { data: T }): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export async function getDepositRequests(
  params: GetDepositRequestsParams,
): Promise<DepositRequestsListResponse> {
  const queryParams: Record<string, string> = {}
  if (params.page) queryParams.page = String(params.page)
  if (params.limit) queryParams.limit = String(params.limit)
  if (params.status) queryParams.status = params.status
  if (params.fromDate) queryParams.fromDate = params.fromDate
  if (params.toDate) queryParams.toDate = params.toDate
  if (params.investorId) queryParams.investorId = params.investorId
  if (params.investorName) queryParams.investorName = params.investorName
  if (params.investorEmail) queryParams.investorEmail = params.investorEmail

  const { data } = await adminApi.get<WireListResponse>(DEPOSIT_REQUESTS_ENDPOINT_PATHS.LIST, {
    params: queryParams,
  })
  return parseDepositRequestsListResponse(unwrap(data))
}

export async function getDepositRequestStatusCounts(): Promise<DepositRequestStatusCounts> {
  const { data } = await adminApi.get<WireStatusCounts>(
    DEPOSIT_REQUESTS_ENDPOINT_PATHS.STATUS_COUNTS,
  )
  return parseDepositRequestStatusCounts(unwrap(data))
}

export async function getDepositRequestDetail(id: string): Promise<DepositRequestDetail> {
  const { data } = await adminApi.get<WireDetailResponse>(
    DEPOSIT_REQUESTS_ENDPOINT_PATHS.DETAIL(id),
  )
  return parseDepositRequestDetail(unwrap(data))
}

export async function approveDepositRequest(
  input: ApproveDepositRequestInput,
): Promise<DepositRequestRow> {
  const payload: ApproveDepositRequestPayloadDto = {
    treasuryBankAccountId: input.treasuryBankAccountId,
    approvedBankReference: input.approvedBankReference,
    idempotencyKey: input.idempotencyKey,
  }
  const { data } = await adminApi.post<WireRowResponse>(
    DEPOSIT_REQUESTS_ENDPOINT_PATHS.APPROVE(input.id),
    payload,
  )
  return parseDepositRequestRow(unwrap(data))
}

export async function rejectDepositRequest(
  input: RejectDepositRequestInput,
): Promise<DepositRequestRow> {
  const payload: RejectDepositRequestPayloadDto = {
    reason: input.reason,
    idempotencyKey: input.idempotencyKey,
  }
  const { data } = await adminApi.post<WireRowResponse>(
    DEPOSIT_REQUESTS_ENDPOINT_PATHS.REJECT(input.id),
    payload,
  )
  return parseDepositRequestRow(unwrap(data))
}
